/**
 * Scrubbing of sensitive query params before anything is handed to third-party analytics.
 * `link` carries the magic-link sign-in credential (exchanged for an access token at
 * `GET /link/<link>`); `code` carries the emailed password-reset / change-password credential
 * (redeemable at `POST /auth/reset-password` — a reset code takes over the account UNauthenticated
 * — and `/auth/change-password-confirm`). A URL holding either must never reach any third-party
 * tracker (Google Analytics, the Facebook Pixel, the Reddit Pixel): a leaked value is a working
 * credential in a third party's logs.
 *
 * Stats > Website keeps the owner's filters (hosts, pages, links, affiliates, campaigns, countries) in its query
 * string, so no third-party tracker sees that query either: Google Analytics gets the route without it, and the pixels
 * and GTM, which read the live URL themselves, are paused while the route is open.
 */

import { statsTabOfPath } from '@/nova/pages/stats/tabs';

/** Query params that must never be sent to a third-party tracker. */
export const SENSITIVE_QUERY_PARAMS = ['link', 'code'];

/** True when a search string (with or without the leading `?`) carries a sensitive param. */
export function hasSensitiveQueryParams(search: string): boolean {
  if (!search) return false;
  const params = new URLSearchParams(search.startsWith('?') ? search.slice(1) : search);
  return SENSITIVE_QUERY_PARAMS.some((name) => params.has(name));
}

/** True on Stats > Website, whose whole query string is the owner's filters. */
export function isFilterRoute(pathname: string): boolean {
  // Read the path as React Router matches it: decoded, ignoring case.
  let path = pathname;
  try {
    path = decodeURIComponent(pathname);
  } catch {
    /* a malformed escape: read the path as typed */
  }
  return statsTabOfPath(path.toLowerCase()) === 'website';
}

/**
 * True when the live URL may reach the trackers that read it themselves with no override (the Facebook and Reddit
 * pixels, GTM's tags): it carries no credential and is not Stats > Website. The bare Stats > Website route counts too:
 * GTM's history listener fires inside `pushState`, before the app can react, so the first filter applied there must
 * already find those trackers paused.
 */
export function trackerSafeUrl(): boolean {
  return !hasSensitiveQueryParams(window.location?.search ?? '') && !isFilterRoute(window.location?.pathname ?? '');
}

/**
 * Strip sensitive query params from a path (`/login?link=…`) or an absolute URL, and Stats > Website's whole query,
 * preserving the input's form. An unparseable input drops its whole query string: fail closed, better a
 * bare path in analytics than a leaked token.
 */
export function scrubAnalyticsUrl(input: string): string {
  const absolute = /^[a-z][a-z0-9+.-]*:/i.test(input);
  try {
    // The base only matters for relative inputs and never appears in the returned value.
    const url = new URL(input, absolute ? undefined : 'http://relative.invalid');
    let changed = false;
    if (isFilterRoute(url.pathname) && url.search) {
      url.search = '';
      changed = true;
    }
    SENSITIVE_QUERY_PARAMS.forEach((name) => {
      if (url.searchParams.has(name)) {
        url.searchParams.delete(name);
        changed = true;
      }
    });
    if (!changed) return input;
    return absolute ? url.href : url.pathname + url.search + url.hash;
  } catch {
    return input.split('?')[0];
  }
}
